import { getFunctions, httpsCallable } from "firebase/functions";

import { firebaseApp } from "./firebase";

export type CentralChecklistGroup =
  | "first_call"
  | "vitals"
  | "cremation"
  | "burial"
  | "veteran"
  | "website"
  | "financial"
  | "documents"
  | "forms"
  | "service"
  | "records";

export type CentralChecklistStatus =
  | "open"
  | "in_progress"
  | "waiting_family"
  | "ready_review"
  | "completed";

export type CentralChecklistTask = {
  id: string;
  automationSource: "manual" | "template" | "automation";
  caseId: string;
  completedAt?: string | null;
  completionMode: "manual" | "source_event";
  completionNote?: string;
  createdAt: string | null;
  dueAt: string | null;
  familyRequestStatus: "none" | "requested";
  funeralHomeId: string;
  group: CentralChecklistGroup;
  ownerName: string;
  status: CentralChecklistStatus;
  title: string;
  updatedAt: string | null;
};

export type CentralChecklistCreateInput = Pick<
  CentralChecklistTask,
  "caseId" | "dueAt" | "group" | "ownerName" | "title"
>;

export type CentralChecklistUpdateInput = Partial<Pick<
  CentralChecklistTask,
  "completionNote" | "dueAt" | "familyRequestStatus" | "group" | "ownerName" | "status" | "title"
>>;

export type CentralPendingChecklistTask = Pick<
  CentralChecklistTask,
  "caseId" | "dueAt" | "group" | "ownerName" | "status" | "title"
> & { id: string; taskId: string };

export type CentralChecklistActivity = {
  actorUid: string;
  createdAt: string | null;
  id: string;
  taskId?: string;
  taskTitle?: string;
  type: string;
};

const functions = getFunctions(firebaseApp, "us-east1");

export async function listCentralChecklist(caseId: string) {
  const callable = httpsCallable<{ caseId: string }, { tasks: CentralChecklistTask[] }>(
    functions,
    "listCentralChecklist",
  );
  return (await callable({ caseId })).data.tasks;
}

export async function listCentralChecklistActivity(caseId: string) {
  const callable = httpsCallable<{ caseId: string }, { events: CentralChecklistActivity[] }>(
    functions,
    "listCentralChecklistActivity",
  );
  return (await callable({ caseId })).data.events;
}

export async function generateCentralChecklist(caseId: string) {
  const callable = httpsCallable<{ caseId: string }, { tasks: CentralChecklistTask[] }>(
    functions,
    "generateCentralChecklist",
  );
  return (await callable({ caseId })).data.tasks;
}

export async function createCentralChecklistTask(input: CentralChecklistCreateInput) {
  const callable = httpsCallable<CentralChecklistCreateInput, { task: CentralChecklistTask }>(
    functions,
    "createCentralChecklistTask",
  );
  return (await callable(input)).data.task;
}

export async function updateCentralChecklistTask(
  caseId: string,
  taskId: string,
  updates: CentralChecklistUpdateInput,
) {
  const callable = httpsCallable<
    { caseId: string; taskId: string; updates: CentralChecklistUpdateInput },
    { task: CentralChecklistTask }
  >(functions, "updateCentralChecklistTask");
  return (await callable({ caseId, taskId, updates })).data.task;
}

export async function deleteCentralChecklistTask(caseId: string, taskId: string) {
  const callable = httpsCallable<
    { caseId: string; taskId: string },
    { taskId: string }
  >(functions, "deleteCentralChecklistTask");
  return (await callable({ caseId, taskId })).data.taskId;
}

export async function listPendingCentralChecklist() {
  const callable = httpsCallable<void, { tasks: CentralPendingChecklistTask[] }>(
    functions,
    "listPendingCentralChecklist",
  );
  return (await callable()).data.tasks;
}

export function getCentralChecklistErrorMessage(error: unknown) {
  const code = typeof error === "object" && error && "code" in error ? String(error.code) : "";
  if (code === "functions/permission-denied" || code === "functions/unauthenticated") {
    return "Your Central session cannot complete this checklist operation. Sign in again or contact an administrator.";
  }
  if (code === "functions/invalid-argument" || code === "functions/failed-precondition") {
    return "Check the task details, transition, and completion note, then try again.";
  }
  if (code === "functions/not-found") {
    return "The selected case or checklist task no longer exists. Refresh this page.";
  }
  return "Central couldn’t save this checklist task. Try again.";
}
